import { useState, type JSX } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { GenerationHelp } from './GenerationHelp.js';
import { Checkbox } from '@/components/ui/checkbox.js';
import type {
  GenerationBasketPreview,
  GenerationPreviewVariable,
} from '@/lib/api/quality-generation-v4.js';

const OMITTED_BINDING_WARNING =
  'Invalid variable bindings were omitted; literal text is unchanged';

export function GenerationBasket({
  preview,
  loading,
  error,
  onToggle,
  onToggleExisting,
  showWarnings = true,
}: {
  preview?: GenerationBasketPreview;
  loading: boolean;
  error?: string;
  onToggle: (
    variable: GenerationPreviewVariable,
    indices: number[],
    enabled: boolean,
  ) => void;
  onToggleExisting?: (key: string, indices: number[], enabled: boolean) => void;
  showWarnings?: boolean;
}): JSX.Element | null {
  const [expanded, setExpanded] = useState(false);
  const existingOnly = Boolean(
    (preview?.existingVariables?.length ||
      preview?.variables.some(
        (variable) => variable.state === 'reuse_candidate',
      )) &&
    !preview?.variables.some(
      (variable) =>
        variable.state === 'new' &&
        variable.caseIndices.some((index) =>
          preview.selection.selectedIndices.includes(index),
        ),
    ),
  );
  if (
    !loading &&
    !error &&
    preview?.variables.length === 0 &&
    !preview.existingVariables?.length
  ) {
    return showWarnings && preview.warnings.length ? (
      <div className="text-xs text-muted-foreground">
        {preview.warnings
          .filter((warning) => warning !== OMITTED_BINDING_WARNING)
          .map((warning) => (
            <p key={warning}>{warning}</p>
          ))}
        {preview.warnings.includes(OMITTED_BINDING_WARNING) ? (
          <details>
            <summary className="cursor-pointer">Details</summary>
            <p>{OMITTED_BINDING_WARNING}</p>
          </details>
        ) : null}
      </div>
    ) : null;
  }
  return (
    <section
      aria-label={existingOnly ? 'Existing variables' : 'Shared variables'}
      className="text-xs"
    >
      <div className="flex items-center gap-1">
        <h3 className="font-medium">
          {existingOnly ? 'Existing variables' : 'Shared variables'}
        </h3>
        <GenerationHelp
          label={
            existingOnly ? 'About existing variables' : 'About shared variables'
          }
          text={
            existingOnly
              ? 'Reuse existing variable values in these cases. Turning one off restores the captured value. Existing variables and environments stay unchanged.'
              : 'Reuse a value across cases. Suggestions are selected by default. Turn one off to keep literal text in that case. Variables are saved only when you create the selected cases and can be overridden when running.'
          }
        />
        <button
          type="button"
          aria-expanded={expanded}
          aria-label="Show shared variable details"
          onClick={() => setExpanded((value) => !value)}
          className="ml-auto inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
        >
          {expanded ? 'Hide details' : 'Details'}
          <ChevronDownIcon
            aria-hidden
            className={`size-3 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </button>
      </div>
      {loading ? <p role="status">Updating preview…</p> : null}
      {error ? (
        <p role="alert" className="text-destructive">
          {error}
        </p>
      ) : null}
      {preview?.existingVariables?.length ? (
        <div aria-label="Existing variables" className="mt-3">
          {!existingOnly ? (
            <h4 className="font-medium">Existing variables</h4>
          ) : null}
          <p className="text-muted-foreground">
            Turn one off to use the value captured for this case. Existing
            variables and environments stay unchanged.
          </p>
          {preview.existingVariables.map((variable) => {
            const indices = variable.caseIndices.filter((index) =>
              preview.selection.selectedIndices.includes(index),
            );
            const enabled = indices.filter((index) =>
              variable.enabledCaseIndices.includes(index),
            );
            return (
              <div key={variable.key} className="py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Checkbox
                    aria-label={`Use existing ${variable.key} in all selected cases`}
                    checked={
                      enabled.length === indices.length && indices.length > 0
                        ? true
                        : enabled.length > 0
                          ? 'indeterminate'
                          : false
                    }
                    disabled={
                      loading || !variable.available || !onToggleExisting
                    }
                    onCheckedChange={(value) =>
                      onToggleExisting?.(variable.key, indices, value === true)
                    }
                  />
                  <code>{variable.key}</code>
                  <code className="ml-auto max-w-full break-all text-muted-foreground">
                    {variable.value ?? 'Frozen value unavailable'}
                  </code>
                </div>
                {expanded ? (
                  <div className="mt-2 flex flex-wrap gap-3">
                    {indices.map((index) => (
                      <div key={index} className="flex items-center gap-1">
                        <Checkbox
                          aria-label={`Use existing ${variable.key} in case ${index + 1}`}
                          checked={variable.enabledCaseIndices.includes(index)}
                          disabled={
                            loading || !variable.available || !onToggleExisting
                          }
                          onCheckedChange={(value) =>
                            onToggleExisting?.(
                              variable.key,
                              [index],
                              value === true,
                            )
                          }
                        />
                        Case {index + 1}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
      {preview?.variables.map((variable) => {
        const indices = variable.caseIndices.filter((index) =>
          preview.selection.selectedIndices.includes(index),
        );
        const enabled = indices.filter((index) =>
          variable.enabledCaseIndices.includes(index),
        );
        return (
          <div key={variable.key} className="py-3">
            <div className="flex flex-wrap items-center gap-2 font-medium">
              <Checkbox
                aria-label={`Use ${variable.effectiveKey} in all selected cases`}
                checked={
                  enabled.length === indices.length && indices.length > 0
                    ? true
                    : enabled.length > 0
                      ? 'indeterminate'
                      : false
                }
                disabled={
                  loading ||
                  variable.state === 'blocked' ||
                  indices.length === 0
                }
                onCheckedChange={(value) =>
                  onToggle(variable, indices, value === true)
                }
              />
              <code>{variable.effectiveKey}</code>
              <code className="ml-auto max-w-full break-all font-normal text-muted-foreground">
                {variable.defaultValue}
              </code>
              <GenerationHelp
                label={`About ${variable.effectiveKey}`}
                text={
                  variable.state === 'reuse_candidate'
                    ? 'Uses an existing project variable with the same value. Saving these cases does not overwrite it.'
                    : 'Creates one shared project variable when you save cases that use it. This preview does not create anything.'
                }
              />
            </div>
            {variable.state === 'blocked' && !expanded ? (
              <p className="mt-1 text-destructive">
                Unavailable: {variable.reason?.replaceAll('_', ' ')}
              </p>
            ) : null}
            {expanded ? (
              <>
                <p className="mt-1 break-all">
                  Default: <code>{variable.defaultValue}</code>
                  <GenerationHelp
                    label={`About the default value of ${variable.effectiveKey}`}
                    text="This is the saved default value. You can override it when launching a run. Turning this variable off restores the literal value in the case."
                  />
                </p>
                <p className="text-muted-foreground">
                  {variable.meaning} · {variable.rationale}
                </p>
                {variable.reason ? (
                  <p className="mt-1 text-muted-foreground">
                    Unavailable: {variable.reason.replaceAll('_', ' ')}
                  </p>
                ) : null}
                <div className="mt-2 flex flex-wrap gap-3">
                  {indices.map((index) => (
                    <div key={index} className="flex items-center gap-1">
                      <Checkbox
                        aria-label={`Use ${variable.effectiveKey} in case ${index + 1}`}
                        checked={variable.enabledCaseIndices.includes(index)}
                        disabled={loading || variable.state === 'blocked'}
                        onCheckedChange={(value) =>
                          onToggle(variable, [index], value === true)
                        }
                      />
                      Case {index + 1}
                    </div>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        );
      })}
      {preview && Object.keys(preview.createVariables).length > 0 ? (
        <p className="mt-3 font-medium">
          Will create {Object.keys(preview.createVariables).length} variable(s)
          in Quality for {preview.cases.length} selected case(s).
        </p>
      ) : null}
      {showWarnings &&
        preview?.warnings
          .filter((warning) => warning !== OMITTED_BINDING_WARNING || expanded)
          .map((warning) => (
            <p key={warning} className="mt-1 text-muted-foreground">
              {warning}
            </p>
          ))}
    </section>
  );
}
